import {
  angleBetweenLines,
  distance,
  getVerticalFoldCrease,
  intersectSegmentsWithinRect,
  limitPointToCircle,
  rotatePoint
} from './geometry';
import type { FlipCorner, FlipDirection, Point, Rect, RectPoints, Segment } from '@/types/book';

/**
 * 翻页几何计算器。
 * 负责把拖拽点转换成翻页角度、页面裁剪区域、底页裁剪区域和阴影切线数据。
 */
export class FlipCalculator {
  private readonly direction: FlipDirection;
  private corner: FlipCorner;
  private readonly pageWidth: number;
  private readonly pageHeight: number;

  /** 翻起页面的旋转角度，单位为弧度。 */
  private angle = 0;
  /** 经过边界限制后的拖拽点，同时也是翻起页面的旋转参考点。 */
  private position: Point = { x: 0, y: 0 };
  /** 翻起页面旋转后的四角坐标，位于页面坐标系内。 */
  private rect: RectPoints;
  /** 翻页边缘与书本上边缘的交点。 */
  private topIntersectPoint: Point | null = null;
  /** 折痕线与书本外侧边缘的交点。 */
  private sideIntersectPoint: Point | null = null;
  /** 翻页边缘与书本下边缘的交点。 */
  private bottomIntersectPoint: Point | null = null;

  /** 创建指定方向、指定角落的翻页计算器。 */
  constructor(
    _direction: FlipDirection,
    _corner: FlipCorner,
    _pageWidth: number,
    _pageHeight: number
  ) {
    this.direction = _direction;
    this.corner = _corner;
    this.pageWidth = _pageWidth;
    this.pageHeight = _pageHeight;
    this.rect = this.getPageRect({ x: this.pageWidth, y: 0 });
  }

  /** 根据页面坐标下的拖拽点执行完整翻页计算。 */
  calc(localPos: Point): boolean {
    try {
      this.position = this.calcAngleAndPosition(localPos);
      this.calculateIntersectPoint(this.position);
      return true;
    } catch {
      return false;
    }
  }

  /** 返回翻起页面的可见裁剪多边形。 */
  getFlippingClipArea(): Point[] {
    const result: Point[] = [];
    let clipBottom = false;

    if (this.topIntersectPoint) {
      result.push(this.rect.topLeft);
      result.push(this.topIntersectPoint);
    }

    if (this.sideIntersectPoint === null) {
      clipBottom = true;
    } else {
      result.push(this.sideIntersectPoint);
      if (this.bottomIntersectPoint === null) clipBottom = false;
    }

    if (this.bottomIntersectPoint) result.push(this.bottomIntersectPoint);

    if (clipBottom || this.corner === 'bottom') {
      result.push(this.rect.bottomLeft);
    }

    return result;
  }

  /** 返回底页在翻页过程中露出的裁剪多边形。 */
  getBottomClipArea(): Point[] {
    const result: Point[] = [];

    if (this.topIntersectPoint) result.push(this.topIntersectPoint);

    if (this.corner === 'top') {
      result.push({ x: this.pageWidth, y: 0 });
    } else {
      if (this.topIntersectPoint !== null) result.push({ x: this.pageWidth, y: 0 });
      result.push({ x: this.pageWidth, y: this.pageHeight });
    }

    // 避免折痕与上交点重合时产生退化多边形。
    if (this.sideIntersectPoint !== null) {
      if (distance(this.sideIntersectPoint, this.topIntersectPoint) >= 10) {
        result.push(this.sideIntersectPoint);
      }
    } else if (this.corner === 'top') {
      result.push({ x: this.pageWidth, y: this.pageHeight });
    }

    if (this.bottomIntersectPoint) result.push(this.bottomIntersectPoint);
    if (this.topIntersectPoint) result.push(this.topIntersectPoint);

    return result;
  }

  /** 返回翻起页面在 Pixi 中应使用的旋转角度。 */
  getAngle(): number {
    return this.direction === 'forward' ? -this.angle : this.angle;
  }

  /** 返回旋转后页面四角坐标。 */
  getRect(): RectPoints {
    return this.rect;
  }

  /** 返回被限制到有效范围内的拖拽点。 */
  getPosition(): Point {
    return this.position;
  }

  /** 返回翻起页面用于定位的锚点。 */
  getActiveCorner(): Point {
    return this.direction === 'forward' ? this.rect.topLeft : this.rect.topRight;
  }

  /** 返回当前翻页方向。 */
  getDirection(): FlipDirection {
    return this.direction;
  }

  /** 返回翻页进度百分比，范围通常为 0 到 100。 */
  getFlippingProgress(): number {
    return Math.abs(((this.position.x - this.pageWidth) / (2 * this.pageWidth)) * 100);
  }

  /** 返回当前操作的页面角。 */
  getCorner(): FlipCorner {
    return this.corner;
  }

  /** 在同一次侧边拖动中切换所使用的物理纸角。 */
  setCorner(corner: FlipCorner): void {
    this.corner = corner;
  }

  /** 返回底页在书本坐标中的初始摆放位置。 */
  getBottomPagePosition(): Point {
    return this.direction === 'back' ? { x: this.pageWidth, y: 0 } : { x: 0, y: 0 };
  }

  /** 返回阴影精灵的起点位置。 */
  getShadowStartPoint(): Point {
    if (this.corner === 'top') return this.topIntersectPoint ?? { x: 0, y: 0 };
    return this.sideIntersectPoint ?? this.topIntersectPoint ?? { x: 0, y: this.pageHeight };
  }

  /** 返回阴影相对页面水平边的旋转角度。 */
  getShadowAngle(): number {
    const angle = angleBetweenLines(this.getSegmentToShadowLine(), [
      { x: 0, y: 0 },
      { x: this.pageWidth, y: 0 }
    ]);

    return this.direction === 'forward' ? angle : Math.PI - angle;
  }

  /** 返回阴影切线线段，供静态层遮罩复用。 */
  getShadowLineSegment(): Segment {
    return this.getSegmentToShadowLine();
  }

  /** 计算翻页角度，并把拖拽点限制在有效翻页范围内。 */
  private calcAngleAndPosition(pos: Point): Point {
    let result = pos;

    this.updateAngleAndGeometry(result);

    if (this.corner === 'top') {
      result = this.checkPositionAtCenterLine(result, { x: 0, y: 0 }, { x: 0, y: this.pageHeight });
    } else {
      result = this.checkPositionAtCenterLine(result, { x: 0, y: this.pageHeight }, { x: 0, y: 0 });
    }

    if (Math.abs(result.x - this.pageWidth) < 1 && Math.abs(result.y) < 1) {
      throw new Error('Point is too small');
    }

    return result;
  }

  /** 根据拖拽点同步更新翻页角度和旋转后页面四角。 */
  private updateAngleAndGeometry(pos: Point): void {
    this.angle = this.calculateAngle(pos);
    this.rect = this.getPageRect(pos);
  }

  /** 根据拖拽点计算纸张折叠角度。 */
  private calculateAngle(pos: Point): number {
    const left = this.pageWidth - pos.x + 1;
    const top = this.corner === 'bottom' ? this.pageHeight - pos.y : pos.y;

    let angle = 2 * Math.acos(left / Math.sqrt(top * top + left * left));

    if (top < 0) angle = -angle;

    const delta = Math.PI - angle;
    if (!Number.isFinite(angle) || (delta >= 0 && delta < 0.003)) {
      throw new Error('The G point is too small');
    }

    if (this.corner === 'bottom') angle = -angle;

    return angle;
  }

  /** 根据当前翻页角生成旋转后的页面四角坐标。 */
  private getPageRect(localPos: Point): RectPoints {
    if (this.corner === 'top') {
      return this.getRectFromBasePoint(
        [
          { x: 0, y: 0 },
          { x: this.pageWidth, y: 0 },
          { x: 0, y: this.pageHeight },
          { x: this.pageWidth, y: this.pageHeight }
        ],
        localPos
      );
    }

    return this.getRectFromBasePoint(
      [
        { x: 0, y: -this.pageHeight },
        { x: this.pageWidth, y: -this.pageHeight },
        { x: 0, y: 0 },
        { x: this.pageWidth, y: 0 }
      ],
      localPos
    );
  }

  /** 将四个基础点围绕当前拖拽点旋转，得到页面四角坐标。 */
  private getRectFromBasePoint(points: Point[], localPos: Point): RectPoints {
    return {
      topLeft: rotatePoint(points[0], localPos, this.angle),
      topRight: rotatePoint(points[1], localPos, this.angle),
      bottomLeft: rotatePoint(points[2], localPos, this.angle),
      bottomRight: rotatePoint(points[3], localPos, this.angle)
    };
  }

  /** 计算翻起页面、折痕线与书本边界的交点。 */
  private calculateIntersectPoint(pos: Point): void {
    const boundRect: Rect = {
      left: -1,
      top: -1,
      width: this.pageWidth + 2,
      height: this.pageHeight + 2
    };

    // 侧边抓取完全水平移动时折痕严格竖直，上下纸边与书页边界重合，
    // 通用直线求交会把它视为退化线。此时折痕就是原纸角与移动纸角的垂直平分线。
    if (Math.abs(this.angle) < 1e-7 && pos.x < this.pageWidth) {
      const [top, bottom] = getVerticalFoldCrease(this.pageWidth, this.pageHeight, pos.x);
      this.topIntersectPoint = top;
      this.sideIntersectPoint = null;
      this.bottomIntersectPoint = bottom;
      return;
    }

    if (this.corner === 'top') {
      this.topIntersectPoint = intersectSegmentsWithinRect(
        boundRect,
        [pos, this.rect.topRight],
        [
          { x: 0, y: 0 },
          { x: this.pageWidth, y: 0 }
        ]
      );

      this.sideIntersectPoint = intersectSegmentsWithinRect(
        boundRect,
        [pos, this.rect.bottomLeft],
        [
          { x: this.pageWidth, y: 0 },
          { x: this.pageWidth, y: this.pageHeight }
        ]
      );

      this.bottomIntersectPoint = intersectSegmentsWithinRect(
        boundRect,
        [this.rect.bottomLeft, this.rect.bottomRight],
        [
          { x: 0, y: this.pageHeight },
          { x: this.pageWidth, y: this.pageHeight }
        ]
      );
    } else {
      this.topIntersectPoint = intersectSegmentsWithinRect(
        boundRect,
        [this.rect.topLeft, this.rect.topRight],
        [
          { x: 0, y: 0 },
          { x: this.pageWidth, y: 0 }
        ]
      );

      this.sideIntersectPoint = intersectSegmentsWithinRect(
        boundRect,
        [pos, this.rect.topLeft],
        [
          { x: this.pageWidth, y: 0 },
          { x: this.pageWidth, y: this.pageHeight }
        ]
      );

      this.bottomIntersectPoint = intersectSegmentsWithinRect(
        boundRect,
        [pos, this.rect.bottomRight],
        [
          { x: 0, y: this.pageHeight },
          { x: this.pageWidth, y: this.pageHeight }
        ]
      );
    }
  }

  /** 将拖拽点限制在以书脊端点为圆心的两个约束圆内。 */
  private checkPositionAtCenterLine(checkedPos: Point, centerOne: Point, centerTwo: Point): Point {
    let result = checkedPos;

    const firstLimited = limitPointToCircle(centerOne, this.pageWidth, result);
    if (result !== firstLimited) {
      result = firstLimited;
      this.updateAngleAndGeometry(result);
    }

    const radius = Math.hypot(this.pageWidth, this.pageHeight);
    const checkPointOne = this.corner === 'bottom' ? this.rect.topRight : this.rect.bottomRight;
    const checkPointTwo = this.corner === 'bottom' ? this.rect.bottomLeft : this.rect.topLeft;

    if (checkPointOne.x <= 0) {
      const secondLimited = limitPointToCircle(centerTwo, radius, checkPointTwo);

      if (secondLimited !== result) {
        result = secondLimited;
        this.updateAngleAndGeometry(result);
      }
    }

    return result;
  }

  /** 返回用于计算阴影角度的线段。 */
  private getSegmentToShadowLine(): Segment {
    const first = this.getShadowStartPoint();
    const second =
      first !== this.sideIntersectPoint && this.sideIntersectPoint !== null
        ? this.sideIntersectPoint
        : (this.bottomIntersectPoint ?? first);

    return [first, second];
  }
}
